import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard,
  DollarSign,
  ExternalLink,
  Calculator,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { siApple } from "simple-icons";

const BrandGlyph = ({
  icon,
  size = 24,
  color,
}: {
  icon: { path: string; hex: string };
  size?: number;
  color?: string;
}) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill={color ?? `#${icon.hex}`}>
    <path d={icon.path} />
  </svg>
);

// Hand-authored — simple-icons still ships the pre-2024 Stripe glyph.
const StripeMark = ({ size = 48 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size}>
    <rect width="24" height="24" rx="5" fill="#635BFF" />
    <path d="M5.4 9.4 L18.6 7.2 L18.6 14.6 L5.4 16.8 Z" fill="#FFFFFF" />
  </svg>
);
import {
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  Button,
  Text,
  Badge,
  Separator,
  Callout,
  Tabs,
} from "@radix-ui/themes";
import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { stripeConnectService } from "@/services/breeze/StripeConnectService";
import StripeConnectSetup from "@/components/anythinkpay/StripeConnectSetup";
import AppleIapSetup from "@/components/anythinkpay/AppleIapSetup";
import {
  ConnectAccountResponse,
  PlatformFeeConfiguration,
  StripeProcessingFeeConfiguration,
} from "@/types/breeze/StripeConnect";

interface PaymentsSettingsProps {
  // "page" (default) renders the full Settings page chrome — title, description, PageTemplate.
  // "tab" renders the tabbed content only, so it can be embedded inline (e.g. the AnythinkPay
  // Overview cold-start) without producing a nested PageTemplate.
  chrome?: "page" | "tab";
}

const StripeConnectPage = withTenantAdminRequired(function StripeConnectPage({
  chrome = "page",
}: PaymentsSettingsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const initialTab = searchParams.get("tab") || "stripe";

  // Check for success parameter from both Express onboarding and Standard OAuth
  const showSuccessMessage =
    searchParams.get("success") === "true" ||
    searchParams.get("stripe_success") === "true";

  // Check for error parameter from Standard OAuth callback
  const oauthError = searchParams.get("stripe_success") === "false"
    ? searchParams.get("error") || "Failed to connect Stripe account."
    : null;

  // Clear success parameters and refresh account status if successful
  useEffect(() => {
    if (showSuccessMessage) {
      queryClient.invalidateQueries({ queryKey: ["stripe-connect-account"] });
      const timer = setTimeout(() => {
        searchParams.delete("success");
        searchParams.delete("stripe_success");
        setSearchParams(searchParams);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, searchParams, setSearchParams, queryClient]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== "stripe-connect-complete") return;
      queryClient.invalidateQueries({ queryKey: ["stripe-connect-account"] });
      if (e.data.success === false && e.data.error) {
        console.error("Stripe Connect OAuth failed:", e.data.error);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);

  // Clear OAuth error parameter after 8 seconds
  useEffect(() => {
    if (oauthError) {
      const timer = setTimeout(() => {
        searchParams.delete("stripe_success");
        searchParams.delete("error");
        setSearchParams(searchParams);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [oauthError, searchParams, setSearchParams]);

  // Query for tenant's Stripe Connect account
  const {
    data: accountData,
    isLoading: isAccountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useQuery<ConnectAccountResponse>({
    queryKey: ["stripe-connect-account"],
    queryFn: () => stripeConnectService.getTenantConnectAccount(),
  });

  // Query for fee configuration
  const {
    data: feeConfig,
    isLoading: isFeeConfigLoading,
    error: feeConfigError,
  } = useQuery<PlatformFeeConfiguration>({
    queryKey: ["stripe-connect-fees"],
    queryFn: () => stripeConnectService.getPlatformFeeConfiguration(),
  });

  // Query for Stripe processing fee configuration
  const {
    data: stripeFeesConfig,
    isLoading: isStripeFeesLoading,
    error: stripeFeesError,
  } = useQuery<StripeProcessingFeeConfiguration>({
    queryKey: ["stripe-processing-fees"],
    queryFn: () => stripeConnectService.getStripeProcessingFeeConfiguration(),
  });

  // Mutation for creating onboarding link
  const { mutateAsync: createOnboardingLink, isPending: isCreatingOnboardingLink } =
    useMutation({
      mutationFn: () => {
        const orgId = window.location.pathname.split("/")[2];
        // Settings → Payments was renamed from /settings/stripe-connect on this branch when the
        // page became tabbed (Stripe / Apple / Fees). The OAuth callback returns the user there.
        const settingsPath = `/org/${orgId}/settings/payments`;
        const callback = (status: "success" | "refresh") =>
          `${window.location.origin}/org/${orgId}/auth/stripe-callback` +
          `?stripe_success=${status === "success" ? "true" : "false"}` +
          `&return_to=${encodeURIComponent(settingsPath)}`;
        return stripeConnectService.createOnboardingLink({
          refresh_url: callback("refresh"),
          return_url: callback("success"),
        });
      },
    });

  const handleStartOnboarding = async () => {
    const width = 600;
    const height = 800;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      "",
      "stripe-connect",
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
    );

    if (popup) {
      popup.document.title = "Connecting Stripe...";
      popup.document.body.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937;">
          <div style="border: 4px solid #f3f3f3; border-top: 4px solid #635BFF; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin-bottom: 16px;"></div>
          <p style="font-size: 16px; font-weight: 500; margin: 0;">Redirecting to Stripe...</p>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </div>
      `;
    }

    try {
      const response = await createOnboardingLink();
      if (popup) {
        popup.location.href = response.url;
      }
    } catch (err) {
      console.error("Failed to start onboarding:", err);
      if (popup) {
        popup.close();
      }
    }
  };

  // Helper function to format requirement field names
  const formatRequirementField = (field: string): string => {
    return field
      .split(".")
      .map((part) =>
        part.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      )
      .join(" → ");
  };

  // Helper function to get requirement status details
  const getRequirementStatus = (account?: ConnectAccountResponse) => {
    if (!account?.account.requirements) return null;

    const req = account.account.requirements;
    const hasCurrentlyDue = (req.currently_due?.length ?? 0) > 0;
    const hasPastDue = (req.past_due?.length ?? 0) > 0;
    const hasPendingVerification = (req.pending_verification?.length ?? 0) > 0;
    const hasEventuallyDue = (req.eventually_due?.length ?? 0) > 0;

    return {
      hasCurrentlyDue,
      hasPastDue,
      hasPendingVerification,
      hasEventuallyDue,
      currentlyDue: req.currently_due ?? [],
      pastDue: req.past_due ?? [],
      pendingVerification: req.pending_verification ?? [],
      eventuallyDue: req.eventually_due ?? [],
      disabledReason: req.disabled_reason,
      currentDeadline: req.current_deadline,
    };
  };

  // Helper function to format deadline
  const formatDeadline = (timestamp: number | null | undefined): string => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getAccountStatus = (account?: ConnectAccountResponse) => {
    if (!account) return null;

    if (account.is_fully_onboarded) {
      return (
        <Badge color="green" size="2" radius="full">
          <CheckCircle size={12} />
          Fully Onboarded
        </Badge>
      );
    } else if (account.account.details_submitted) {
      return (
        <Badge color="orange" size="2" radius="full">
          <AlertCircle size={12} />
          Pending Verification
        </Badge>
      );
    } else {
      return (
        <Badge color="red" size="2" radius="full">
          <XCircle size={12} />
          Setup Required
        </Badge>
      );
    }
  };

  const getStatusIcon = (account?: ConnectAccountResponse) => {
    if (!account) return null;

    if (account.is_fully_onboarded) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (account.account.details_submitted) {
      return <AlertCircle className="w-5 h-5 text-orange-600" />;
    } else {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  if (isFeeConfigLoading || isStripeFeesLoading) return <LoadingScreen />;
  if (feeConfigError || stripeFeesError)
    return <div>Error loading Stripe Connect settings</div>;

  const tabsContent = (
    <Tabs.Root defaultValue={initialTab}>
        <Tabs.List size="2">
          <Tabs.Trigger value="stripe">Stripe</Tabs.Trigger>
          <Tabs.Trigger value="apple">Apple</Tabs.Trigger>
          <Tabs.Trigger value="calculator">Fees &amp; Calculator</Tabs.Trigger>
        </Tabs.List>

        <Box pt="5">
          <Tabs.Content value="stripe">
            {/* Stripe Section */}
            <Card size="4" variant="classic">
          <Flex align="center" gap="4" mb="6">
            <StripeMark size={48} />
            <Box>
              <Heading as="h2" size="5" weight="bold" mb="1">
                Stripe
              </Heading>
              <Text size="3" color="gray">
                {accountData?.account
                  ? "Manage your connected Stripe account"
                  : "Connect with Stripe to start processing payments"}
              </Text>
            </Box>
          </Flex>

          {isAccountLoading ? (
            <Flex align="center" justify="center" py="8">
              <LoadingScreen />
            </Flex>
          ) : accountError ? (
            <Callout.Root color="red" size="1">
              <Callout.Icon>
                <XCircle size={16} />
              </Callout.Icon>
              <Callout.Text>
                Error loading account details. Please try again.
              </Callout.Text>
            </Callout.Root>
          ) : accountData?.account ? (
            <Flex direction="column" gap="6">
              {/* Success Message */}
              {showSuccessMessage && (
                <Callout.Root color="green" size="2">
                  <Callout.Icon>
                    <CheckCircle size={16} />
                  </Callout.Icon>
                  <Callout.Text>
                    <Text weight="bold">
                      Your account information has been submitted successfully!
                    </Text>
                    <br />
                    Stripe is now reviewing your account. This process typically
                    takes a few minutes to a few hours. You'll be able to accept
                    payments once verification is complete.
                  </Callout.Text>
                </Callout.Root>
              )}

              {/* Account Overview */}
              <Box
                p="4"
                style={{
                  backgroundColor: "var(--gray-2)",
                  borderRadius: "8px",
                }}
              >
                <Flex align="center" justify="between" mb="4">
                  <Flex align="center" gap="3">
                    {getStatusIcon(accountData)}
                    <Flex direction="column">
                      <Text size="4" weight="bold" mb="1">
                        {accountData.account.business_profile?.name ||
                          accountData.account.email ||
                          "Unnamed Account"}
                      </Text>
                      <Text size="2" color="gray">
                        Account ID: {accountData.account.id}
                      </Text>
                    </Flex>
                  </Flex>
                  {getAccountStatus(accountData)}
                </Flex>

                <Grid columns={{ initial: "1", sm: "2" }} gap="4">
                  <Box
                    p="3"
                    style={{
                      backgroundColor: "var(--color-panel-solid)",
                      borderRadius: "6px",
                    }}
                  >
                    <Text size="2" weight="bold" color="gray" mb="1">
                      Charges
                    </Text>
                    <Flex align="center" gap="2">
                      {accountData.account.charges_enabled ? (
                        <CheckCircle
                          size={16}
                          style={{ color: "var(--green-9)" }}
                        />
                      ) : (
                        <XCircle size={16} style={{ color: "var(--red-9)" }} />
                      )}
                      <Text
                        size="3"
                        weight="medium"
                        color={
                          accountData.account.charges_enabled ? "green" : "red"
                        }
                      >
                        {accountData.account.charges_enabled
                          ? "Enabled"
                          : "Disabled"}
                      </Text>
                    </Flex>
                  </Box>
                  <Box
                    p="3"
                    style={{
                      backgroundColor: "var(--color-panel-solid)",
                      borderRadius: "6px",
                    }}
                  >
                    <Text size="2" weight="bold" color="gray" mb="1">
                      Payouts
                    </Text>
                    <Flex align="center" gap="2">
                      {accountData.account.payouts_enabled ? (
                        <CheckCircle
                          size={16}
                          style={{ color: "var(--green-9)" }}
                        />
                      ) : (
                        <XCircle size={16} style={{ color: "var(--red-9)" }} />
                      )}
                      <Text
                        size="3"
                        weight="medium"
                        color={
                          accountData.account.payouts_enabled ? "green" : "red"
                        }
                      >
                        {accountData.account.payouts_enabled
                          ? "Enabled"
                          : "Disabled"}
                      </Text>
                    </Flex>
                  </Box>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Flex gap="3" wrap="wrap">
                {!accountData.is_fully_onboarded && (
                  <Button
                    onClick={handleStartOnboarding}
                    loading={isCreatingOnboardingLink}
                    size="3"
                    variant="solid"
                  >
                    <ExternalLink size={16} />
                    Complete Account Setup
                  </Button>
                )}
                <Button
                  onClick={() => refetchAccount()}
                  variant="outline"
                  size="3"
                >
                  <RefreshCw size={16} />
                  Refresh Status
                </Button>
              </Flex>

              {/* Requirements Details */}
              {!accountData.is_fully_onboarded &&
                (() => {
                  const reqStatus = getRequirementStatus(accountData);
                  if (!reqStatus) return null;

                  return (
                    <Flex direction="column" gap="4">
                      {/* Disabled Reason Callout */}
                      {reqStatus.disabledReason && (
                        <Callout.Root color="red" size="2">
                          <Callout.Icon>
                            <AlertTriangle size={16} />
                          </Callout.Icon>
                          <Callout.Text>
                            <Text weight="bold">Account Disabled: </Text>
                            {reqStatus.disabledReason ===
                            "requirements.pending_verification"
                              ? "Your account is pending verification. Stripe is reviewing your submitted information."
                              : reqStatus.disabledReason ===
                                  "requirements.past_due"
                                ? "Your account has past due requirements that need to be completed immediately."
                                : reqStatus.disabledReason}
                          </Callout.Text>
                        </Callout.Root>
                      )}

                      {/* Deadline Warning */}
                      {reqStatus.currentDeadline && (
                        <Callout.Root color="orange" size="2">
                          <Callout.Icon>
                            <Clock size={16} />
                          </Callout.Icon>
                          <Callout.Text>
                            <Text weight="bold">Action Required by: </Text>
                            {formatDeadline(reqStatus.currentDeadline)}
                            {" - "}
                            Please complete the required information before this
                            deadline to keep your account active.
                          </Callout.Text>
                        </Callout.Root>
                      )}

                      {/* Pending Verification */}
                      {reqStatus.hasPendingVerification && (
                        <Card variant="surface">
                          <Flex direction="column" gap="3" p="4">
                            <Flex align="center" gap="2">
                              <Clock
                                size={20}
                                style={{ color: "var(--orange-9)" }}
                              />
                              <Text size="4" weight="bold" color="orange">
                                Pending Verification (
                                {reqStatus.pendingVerification.length})
                              </Text>
                            </Flex>
                            <Text size="2" color="gray">
                              The following information has been submitted and
                              is being verified by Stripe:
                            </Text>
                            <Box
                              p="3"
                              style={{
                                backgroundColor: "var(--gray-2)",
                                borderRadius: "6px",
                              }}
                            >
                              <Flex direction="column" gap="2">
                                {reqStatus.pendingVerification.map(
                                  (field, index) => (
                                    <Flex
                                      key={index}
                                      align="center"
                                      gap="2"
                                      style={{
                                        paddingBottom:
                                          index <
                                          reqStatus.pendingVerification.length -
                                            1
                                            ? "8px"
                                            : "0",
                                        borderBottom:
                                          index <
                                          reqStatus.pendingVerification.length -
                                            1
                                            ? "1px solid var(--gray-5)"
                                            : "none",
                                      }}
                                    >
                                      <Clock
                                        size={14}
                                        style={{ color: "var(--gray-9)" }}
                                      />
                                      <Text size="2">
                                        {formatRequirementField(field)}
                                      </Text>
                                    </Flex>
                                  ),
                                )}
                              </Flex>
                            </Box>
                          </Flex>
                        </Card>
                      )}

                      {/* Past Due Requirements */}
                      {reqStatus.hasPastDue && (
                        <Card variant="surface">
                          <Flex direction="column" gap="3" p="4">
                            <Flex align="center" gap="2">
                              <AlertTriangle
                                size={20}
                                style={{ color: "var(--red-9)" }}
                              />
                              <Text size="4" weight="bold" color="red">
                                Past Due ({reqStatus.pastDue.length})
                              </Text>
                            </Flex>
                            <Text size="2" color="gray">
                              These requirements are overdue and must be
                              completed immediately:
                            </Text>
                            <Box
                              p="3"
                              style={{
                                backgroundColor: "var(--red-2)",
                                borderRadius: "6px",
                                border: "1px solid var(--red-6)",
                              }}
                            >
                              <Flex direction="column" gap="2">
                                {reqStatus.pastDue.map((field, index) => (
                                  <Flex
                                    key={index}
                                    align="center"
                                    gap="2"
                                    style={{
                                      paddingBottom:
                                        index < reqStatus.pastDue.length - 1
                                          ? "8px"
                                          : "0",
                                      borderBottom:
                                        index < reqStatus.pastDue.length - 1
                                          ? "1px solid var(--red-5)"
                                          : "none",
                                    }}
                                  >
                                    <AlertTriangle
                                      size={14}
                                      style={{ color: "var(--red-9)" }}
                                    />
                                    <Text size="2" weight="medium">
                                      {formatRequirementField(field)}
                                    </Text>
                                  </Flex>
                                ))}
                              </Flex>
                            </Box>
                          </Flex>
                        </Card>
                      )}

                      {/* Currently Due Requirements */}
                      {reqStatus.hasCurrentlyDue && (
                        <Card variant="surface">
                          <Flex direction="column" gap="3" p="4">
                            <Flex align="center" gap="2">
                              <AlertCircle
                                size={20}
                                style={{ color: "var(--amber-9)" }}
                              />
                              <Text size="4" weight="bold" color="amber">
                                Currently Due ({reqStatus.currentlyDue.length})
                              </Text>
                            </Flex>
                            <Text size="2" color="gray">
                              These requirements need to be completed to
                              activate your account:
                            </Text>
                            <Box
                              p="3"
                              style={{
                                backgroundColor: "var(--amber-2)",
                                borderRadius: "6px",
                                border: "1px solid var(--amber-6)",
                              }}
                            >
                              <Flex direction="column" gap="2">
                                {reqStatus.currentlyDue.map((field, index) => (
                                  <Flex
                                    key={index}
                                    align="center"
                                    gap="2"
                                    style={{
                                      paddingBottom:
                                        index <
                                        reqStatus.currentlyDue.length - 1
                                          ? "8px"
                                          : "0",
                                      borderBottom:
                                        index <
                                        reqStatus.currentlyDue.length - 1
                                          ? "1px solid var(--amber-5)"
                                          : "none",
                                    }}
                                  >
                                    <AlertCircle
                                      size={14}
                                      style={{ color: "var(--amber-9)" }}
                                    />
                                    <Text size="2" weight="medium">
                                      {formatRequirementField(field)}
                                    </Text>
                                  </Flex>
                                ))}
                              </Flex>
                            </Box>
                          </Flex>
                        </Card>
                      )}

                      {/* Eventually Due Requirements */}
                      {reqStatus.hasEventuallyDue && (
                        <Card variant="surface">
                          <Flex direction="column" gap="3" p="4">
                            <Flex align="center" gap="2">
                              <Clock
                                size={20}
                                style={{ color: "var(--blue-9)" }}
                              />
                              <Text size="4" weight="bold" color="blue">
                                Eventually Due ({reqStatus.eventuallyDue.length}
                                )
                              </Text>
                            </Flex>
                            <Text size="2" color="gray">
                              These requirements will need to be completed in
                              the future:
                            </Text>
                            <Box
                              p="3"
                              style={{
                                backgroundColor: "var(--blue-2)",
                                borderRadius: "6px",
                              }}
                            >
                              <Flex direction="column" gap="2">
                                {reqStatus.eventuallyDue.map((field, index) => (
                                  <Flex
                                    key={index}
                                    align="center"
                                    gap="2"
                                    style={{
                                      paddingBottom:
                                        index <
                                        reqStatus.eventuallyDue.length - 1
                                          ? "8px"
                                          : "0",
                                      borderBottom:
                                        index <
                                        reqStatus.eventuallyDue.length - 1
                                          ? "1px solid var(--blue-5)"
                                          : "none",
                                    }}
                                  >
                                    <Clock
                                      size={14}
                                      style={{ color: "var(--blue-9)" }}
                                    />
                                    <Text size="2">
                                      {formatRequirementField(field)}
                                    </Text>
                                  </Flex>
                                ))}
                              </Flex>
                            </Box>
                          </Flex>
                        </Card>
                      )}

                      {/* General Status Message */}
                      {!reqStatus.hasPastDue &&
                        !reqStatus.hasCurrentlyDue &&
                        !reqStatus.hasPendingVerification &&
                        !reqStatus.hasEventuallyDue && (
                          <Callout.Root color="blue" size="2">
                            <Callout.Icon>
                              <CheckCircle size={16} />
                            </Callout.Icon>
                            <Callout.Text>
                              Your account is being processed. No additional
                              requirements at this time.
                            </Callout.Text>
                          </Callout.Root>
                        )}
                    </Flex>
                  );
                })()}
            </Flex>
          ) : (
            <Box>
              {oauthError && (
                <Callout.Root mb="4" size="2" color="red">
                  <Callout.Icon><AlertTriangle size={16} /></Callout.Icon>
                  <Callout.Text>
                    <Text weight="bold">Stripe Connect Error: </Text>
                    {oauthError}
                  </Callout.Text>
                </Callout.Root>
              )}

              <Callout.Root mb="6" size="1">
                <Callout.Icon>
                  <CreditCard size={16} />
                </Callout.Icon>
                <Callout.Text>
                  Set up Stripe to start processing payments. Create a new account or connect an existing one.
                </Callout.Text>
              </Callout.Root>

              <StripeConnectSetup />
            </Box>
          )}
            </Card>
          </Tabs.Content>

          <Tabs.Content value="apple">
            {/* Apple In-App Purchase (iOS) Section */}
            <Card size="4" variant="classic">
              <Flex align="center" gap="4" mb="6">
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "#F5F5F7",
                  }}
                >
                  <BrandGlyph icon={siApple} size={26} />
                </Flex>
                <Box>
                  <Heading as="h2" size="5" weight="bold" mb="1">
                    Apple In-App Purchase (iOS)
                  </Heading>
                  <Text size="3" color="gray">
                    Configure App Store Connect so iOS purchases verify against Anythink
                  </Text>
                </Box>
              </Flex>

              <AppleIapSetup />
            </Card>
          </Tabs.Content>

          <Tabs.Content value="calculator">
            <Grid columns={{ initial: "1", lg: "2" }} gap="6">
          {/* Fee Configuration */}
          <Card size="4" variant="classic">
            <Flex align="center" gap="4" mb="6">
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "var(--accent-3)",
                }}
              >
                <DollarSign size={20} style={{ color: "var(--accent-9)" }} />
              </Flex>
              <Box>
                <Heading as="h3" size="4" weight="bold" mb="1">
                  Platform Fees
                </Heading>
                <Text size="2" color="gray">
                  Current fee structure for your platform
                </Text>
              </Box>
            </Flex>

            {feeConfig ? (
              <Flex direction="column" gap="4">
                <Box>
                  <Flex direction="column" gap="2">
                    <Text size="3" weight="bold" mb="3">
                      Platform Fees
                    </Text>
                    {feeConfig.percentage_fee && (
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            Percentage Fee
                          </Text>
                          <Text size="3" weight="bold">
                            {feeConfig.percentage_fee.toFixed(2)}%
                          </Text>
                        </Flex>
                      </Box>
                    )}
                    {feeConfig.fixed_fee && (
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            Fixed Fee
                          </Text>
                          <Text size="3" weight="bold">
                            £{(feeConfig.fixed_fee / 100).toFixed(2)}
                          </Text>
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                </Box>

                <Separator size="2" />

                <Box>
                  <Flex direction="column" gap="2" mb="2"></Flex>
                  {stripeFeesConfig ? (
                    <Flex direction="column" gap="2">
                      <Text size="3" weight="bold">
                        Stripe Processing Fees (by Card Origin)
                      </Text>
                      <Text size="2" color="gray">
                        Fees are based on where the card was issued, not the
                        payment currency.
                      </Text>
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            {stripeFeesConfig.uk_domestic.description}
                          </Text>
                          <Text size="3" weight="bold">
                            {stripeFeesConfig.uk_domestic.percentage_fee.toFixed(
                              2,
                            )}
                            % + £
                            {stripeFeesConfig.uk_domestic.fixed_fee.toFixed(2)}
                          </Text>
                        </Flex>
                      </Box>
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            {stripeFeesConfig.uk_premium.description}
                          </Text>
                          <Text size="3" weight="bold">
                            {stripeFeesConfig.uk_premium.percentage_fee.toFixed(
                              2,
                            )}
                            % + £
                            {stripeFeesConfig.uk_premium.fixed_fee.toFixed(2)}
                          </Text>
                        </Flex>
                      </Box>
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            {stripeFeesConfig.eea.description}
                          </Text>
                          <Text size="3" weight="bold">
                            {stripeFeesConfig.eea.percentage_fee.toFixed(2)}% +
                            £{stripeFeesConfig.eea.fixed_fee.toFixed(2)}
                            {stripeFeesConfig.eea.currency_conversion_fee >
                              0 && (
                              <Text size="1" color="gray" ml="1">
                                (+{stripeFeesConfig.eea.currency_conversion_fee}
                                % if currency conversion)
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      </Box>
                      <Box
                        p="3"
                        style={{
                          backgroundColor: "var(--gray-2)",
                          borderRadius: "6px",
                        }}
                      >
                        <Flex justify="between" align="center">
                          <Text size="2" color="gray">
                            {stripeFeesConfig.international.description}
                          </Text>
                          <Text size="3" weight="bold">
                            {stripeFeesConfig.international.percentage_fee.toFixed(
                              2,
                            )}
                            % + £
                            {stripeFeesConfig.international.fixed_fee.toFixed(
                              2,
                            )}
                            {stripeFeesConfig.international
                              .currency_conversion_fee > 0 && (
                              <Text size="1" color="gray" ml="1">
                                (+
                                {
                                  stripeFeesConfig.international
                                    .currency_conversion_fee
                                }
                                % if currency conversion)
                              </Text>
                            )}
                          </Text>
                        </Flex>
                      </Box>
                    </Flex>
                  ) : (
                    <Callout.Root color="gray" size="1">
                      <Callout.Text>
                        Unable to load Stripe processing fee configuration.
                      </Callout.Text>
                    </Callout.Root>
                  )}
                </Box>
              </Flex>
            ) : (
              <Callout.Root color="gray" size="1">
                <Callout.Text>
                  No fee configuration found. Contact support to set up your
                  platform fees.
                </Callout.Text>
              </Callout.Root>
            )}
          </Card>

          {/* Fee Calculator */}
          <Card size="4" variant="classic">
            <Flex align="center" gap="4" mb="6">
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "var(--accent-3)",
                }}
              >
                <Calculator size={20} style={{ color: "var(--accent-9)" }} />
              </Flex>
              <Box>
                <Heading as="h3" size="4" weight="bold" mb="1">
                  Fee Calculator
                </Heading>
                <Text size="2" color="gray">
                  Calculate estimated total fees (platform + Stripe) for any
                  transaction
                </Text>
              </Box>
            </Flex>

            <Callout.Root color="blue" size="1" mb="4">
              <Callout.Text>
                <strong>How fees work:</strong> When a customer pays, both
                platform fees and Stripe processing fees are deducted before
                transferring the remaining amount to the connected merchant
                account.
              </Callout.Text>
            </Callout.Root>

            <Callout.Root color="amber" size="1" mb="4">
              <Callout.Text>
                <strong>Important:</strong> This calculator uses worst-case
                Stripe fees (international cards + currency conversion) for
                conservative estimates. Actual fees depend on the customer's
                card origin and may change.
              </Callout.Text>
            </Callout.Root>
          </Card>
            </Grid>
          </Tabs.Content>
        </Box>
      </Tabs.Root>
  );

  if (chrome === "tab") return tabsContent;

  return (
    <PageTemplate
      title="Payments"
      description="Configure how this organisation takes payments — Stripe for web and mobile, Apple In-App Purchase for iOS, with more providers to come"
    >
      {tabsContent}
    </PageTemplate>
  );
});

export default StripeConnectPage;
